import './UserCell.sass';

import React, { FC, MouseEvent, useMemo } from 'react';
import { Link, useRouter } from 'react-router5';

import { UserCellProps } from '$shared/components/UserCell/UserCell.interface';
import { useCurrentUserInfo, useGroupInfo, useTranslator, useUserInfo } from '$hooks';
import { classNames } from '$utils';
import { CHAT, GROUP, PROFILE, RESOLVE } from '$shared/constants/pages';
import { UnitName, UnitPhoto, Verified } from '$shared/components';
import { Button } from '$uikit';
import { IcAdd28, IcClose28, IcDone28, IcMessage28, IcMore24 } from '$assets';
import { useDispatch, useSelector } from 'react-redux';
import { followersActions, followersSelector } from '$store/followers';
import { editGroupActions } from '$store/editGroup';

enum UserCellActions {
  FOLLOW = 'follow',
  UNFOLLOW = 'unfollow',
  PROMOTE = 'promote',
  CHAT = 'chat',
}

export const UserCell: FC<UserCellProps> = (props) => {
  const {
    objectId,
    isGroup = false,
    isFollowBtnShown = true,
    caption = null,
    isPromote = false,
    targetId = 0,
    skipButton = false,
    disabled = false,
    after = null,
    before = null,
    isMoreBtnShown,
    onMoreBtnClick,
    isMessageBtnShown = true,
    skipPadding = false,
  } = props;
  const dispatch = useDispatch();
  const user = useUserInfo(objectId);
  const group = useGroupInfo(objectId);
  const curUser = useCurrentUserInfo();
  const { following } = useSelector(followersSelector);
  const t = useTranslator();
  const router = useRouter();
  const isUserActionBtnShown =
    !skipButton &&
    (isFollowBtnShown || isPromote) &&
    (curUser.id !== objectId || isGroup);

  const isFollowed = useMemo(() => {
    if (isGroup) {
      return following[-objectId];
    } else {
      return following[objectId];
    }
  }, [following, objectId, isGroup]);

  const info = useMemo(() => {
    if (isGroup) {
      return {
        followers: group.followers,
        photo: group.photo,
        route: group.domain ? RESOLVE : GROUP,
        routeParams: group.domain ? { domain: group.domain } : { id: group.id },
      };
    } else {
      return {
        followers: user.followers,
        photo: user.photo,
        route: user.domain ? RESOLVE : PROFILE,
        routeParams: user.domain ? { domain: user.domain } : { id: user.id },
      };
    }
  }, [user, group, isGroup]);

  const handleAction = (e: MouseEvent, action: UserCellActions) => {
    switch (action) {
      case UserCellActions.FOLLOW:
        dispatch(followersActions.follow(isGroup ? -objectId : objectId));
        break;
      case UserCellActions.UNFOLLOW:
        dispatch(followersActions.unfollow(isGroup ? -objectId : objectId));
        break;
      case UserCellActions.PROMOTE:
        dispatch(
          editGroupActions.promoteAdmin({
            groupId: -targetId,
            userId: objectId,
          }),
        );
        break;
      case UserCellActions.CHAT:
        router.navigate(CHAT, { peerId: objectId });
    }
  };

  const handleCellClick = (e) => {
    if (e.target.closest('.UserCell__follow')) {
      e.preventDefault();
    }
  };

  return (
    <Link
      className={classNames({
        UserCell: true,
        disabled,
        skipPadding,
      })}
      routeName={info.route}
      routeParams={info.routeParams}
      onClick={handleCellClick}
    >
      {before}
      <UnitPhoto unit={isGroup ? group : user} />
      <div className="UserCell__cont">
        <div className="UserCell__name_wrap">
          <div className="UserCell__name">
            <UnitName unit={isGroup ? group : user} />
          </div>
          <Verified sponsor={props.isSponsor} objectId={isGroup ? -objectId : objectId} />
        </div>
        <div className="UserCell__caption">
          {caption || t('followed_count', { count: info.followers })}
        </div>
      </div>
      {isUserActionBtnShown &&
        (isPromote || (isFollowed && objectId > 0 && isMessageBtnShown)) && (
          <div className="UserCell__follow">
            <Button
              icon={isPromote ? <IcDone28 /> : <IcMessage28 />}
              size="small"
              mode={isFollowed ? 'secondary' : 'default'}
              onClick={(e) =>
                handleAction(
                  e,
                  isPromote ? UserCellActions.PROMOTE : UserCellActions.CHAT,
                )
              }
            />
          </div>
        )}
      {isUserActionBtnShown && (
        <div className="UserCell__follow">
          <Button
            icon={isFollowed ? <IcClose28 /> : <IcAdd28 />}
            size="small"
            mode={isFollowed ? 'secondary' : 'default'}
            onClick={(e) =>
              handleAction(
                e,
                isFollowed ? UserCellActions.UNFOLLOW : UserCellActions.FOLLOW,
              )
            }
          />
        </div>
      )}
      {isMoreBtnShown && (
        <div
          className="UserCell__actionBtn"
          onClick={(e) => {
            e.preventDefault();
            onMoreBtnClick && onMoreBtnClick(objectId);
          }}
        >
          <IcMore24 />
        </div>
      )}
      {after}
    </Link>
  );
};
